import { forwardRef } from 'react';
import { ScrollX } from '../components/ScrollX.jsx';

/**
 * SURFACE-COHERE (item C+E): the dashboard "How to read this report" panel.
 *
 * The PDF has always carried a "How to read this report" front-matter page
 * (methodology.ejs); the dashboard had no equivalent surface, so the engine's
 * methodology block (projectInfo.methodology + methodologyNote) and the new
 * verdictRule never reached a reader on the web. This panel renders the SAME
 * engine strings verbatim (Rule 12) — methodologyNote, methodology.intro, the
 * per-family population table, the redistribution note and the verdictRule —
 * so both surfaces explain the report identically.
 *
 * Collapsed by default; the header toggles it, and the verdict banner's
 * "How is the verdict decided?" link opens it and scrolls it into view.
 */
const fmt = (n) => Number(n).toLocaleString('en-GB');

export const HowToReadPanel = forwardRef(function HowToReadPanel(
  { methodologyNote, methodology, open, onToggle },
  ref,
) {
  const m = methodology || {};
  const families = m.families || [];
  // Nothing engine-emitted → render nothing (a stored legacy report predating
  // the methodology block, or a degenerate analysis).
  if (!methodologyNote && !m.intro && families.length === 0 && !m.verdictRule) {
    return null;
  }

  return (
    <section className="how-to-read" ref={ref} aria-label="How to read this report">
      <button
        type="button"
        className="how-to-read-header"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="how-to-read-title">How to read this report</span>
        <span className="how-to-read-chevron" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="how-to-read-body">
          {methodologyNote && (
            <p className="how-to-read-note">{methodologyNote}</p>
          )}
          {m.intro && (
            <p className="how-to-read-note">{m.intro}</p>
          )}

          {families.length > 0 && (
            /* MOBILE-FIT M2: slides if it ever overflows at narrow widths. */
            <ScrollX>
              <table className="how-to-read-table">
                <tbody>
                  {families.map((f) => (
                    <tr key={f.key}>
                      <td className="htr-family">{f.label}</td>
                      <td className="htr-population">
                        <span className="htr-count">{fmt(f.population)}</span>
                        <span className="htr-unit"> activities</span>
                      </td>
                      <td className="htr-basis">{f.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}

          <p className="how-to-read-note how-to-read-secondary">
            Percentages and counts on each check are taken against its own population above.
            {m.redistributionNote ? ` ${m.redistributionNote}` : ''}
          </p>

          {m.verdictRule && (
            <div className="how-to-read-verdict" id="how-is-the-verdict-decided">
              <div className="how-to-read-subhead">How is the verdict decided?</div>
              <p className="how-to-read-note how-to-read-secondary">{m.verdictRule}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
});
